// Shared rules for public.vendor_project_links: which register firm worked on
// which BRIO project. The Contacts register (BRIO Project column) and the Comms
// project facts drawer (collaborators) both read and write that one table, so
// the rules for turning its rows into what each page shows live here, pure and
// unit tested.

const sameText = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

// Label for a link whose firm has no specialism on the register yet.
export const NO_SPECIALISM = 'Specialism not set';

// A firm's specialism field may hold several, comma separated
// ("Tile & Ceramics, Natural Stone").
export function splitSpecialisms(value) {
  return String(value || '').split(',').map(s => s.trim()).filter(Boolean);
}

export function primarySpecialism(vendor) {
  return splitSpecialisms(vendor?.specialism)[0] || '';
}

// A link with no specialism of its own reads as the firm's first one, so a
// project ticked on the register lands under the right discipline in Comms
// without anyone choosing it.
export function effectiveSpecialism(link, vendor) {
  const own = String(link?.specialism || '').trim();
  return own || primarySpecialism(vendor) || NO_SPECIALISM;
}

// What to store when Comms credits a firm under a specialism: nothing when it
// is the firm's own first specialism (the link then follows the register),
// the label otherwise.
export function specialismToStore(vendor, specialism) {
  return sameText(primarySpecialism(vendor), specialism) ? null : String(specialism || '').trim() || null;
}

// Comms rows: one per specialism in use on the project, plus any the user has
// opened but not filled. Ordered by the specialism list (category, then label),
// with labels no longer in the list after, and "not set" last.
export function specialismRows(links, vendorsById, pending, options) {
  const labels = new Map(); // lower -> display
  links.forEach(l => {
    const v = vendorsById.get(Number(l.vendor_id));
    if (!v) return; // a firm deleted from the register stops rendering
    const s = effectiveSpecialism(l, v);
    if (!labels.has(s.toLowerCase())) labels.set(s.toLowerCase(), s);
  });
  (pending || []).forEach(s => { if (!labels.has(s.toLowerCase())) labels.set(s.toLowerCase(), s); });
  const order = (options || []).map(o => o.label.toLowerCase());
  const rank = s => {
    if (s.toLowerCase() === NO_SPECIALISM.toLowerCase()) return Number.MAX_SAFE_INTEGER;
    const i = order.indexOf(s.toLowerCase());
    return i === -1 ? order.length : i;
  };
  return [...labels.values()].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

// Specialism options in the order Comms offers them: grouped by register
// category, alphabetical inside each.
export const SPECIALISM_CATEGORY_ORDER = ['Consultants', 'Contractor', 'Vendor / Supplier', 'Media'];
export function sortSpecialismOptions(options) {
  const catRank = c => {
    const i = SPECIALISM_CATEGORY_ORDER.indexOf(c);
    return i === -1 ? SPECIALISM_CATEGORY_ORDER.length : i;
  };
  return [...(options || [])].sort((a, b) =>
    catRank(a.category) - catRank(b.category) || String(a.label).localeCompare(String(b.label)));
}

// Firms offered while typing in a specialism row. Name matches rank above
// specialism matches, and a match at the start of the name or of a word in it
// above one in the middle. Before anything is typed, the firms that carry this
// specialism on the register are offered.
export function searchVendors(vendors, query, specialism, limit = 8) {
  const q = String(query || '').trim().toLowerCase();
  const scored = [];
  (vendors || []).forEach(v => {
    const name = String(v.company_name || '').toLowerCase();
    const specs = splitSpecialisms(v.specialism).map(s => s.toLowerCase());
    let score = -1;
    if (q) {
      if (name.startsWith(q)) score = 4;
      else if (name.split(/[\s\-&/.,()]+/).some(w => w.startsWith(q))) score = 3;
      else if (name.includes(q)) score = 2;
      else if (specs.some(s => s.includes(q))) score = 1;
    } else if (specialism && specs.includes(String(specialism).toLowerCase())) {
      score = 1;
    }
    if (score >= 0) scored.push({ v, score, name });
  });
  scored.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
  return scored.slice(0, limit).map(s => s.v);
}

// ── Register side ──────────────────────────────────────────────────────────

// The register's BRIO Project cell is a comma separated list of names. Split
// it into project ids (names that match a project) and legacy text (names
// that match nothing, kept on vendors.brio_project so they stay visible).
export function resolveProjectNames(value, projects) {
  const byName = new Map();
  (projects || []).forEach(p => {
    const key = String(p.name || '').trim().toLowerCase();
    // A live project wins over a deleted one of the same name.
    if (key && (!byName.has(key) || (byName.get(key).deleted && !p.deleted))) {
      byName.set(key, { id: Number(p.id), deleted: !!p.deleted });
    }
  });
  const ids = [];
  const legacy = [];
  String(value || '').split(',').map(s => s.trim()).filter(Boolean).forEach(name => {
    const hit = byName.get(name.toLowerCase());
    if (hit) { if (!ids.includes(hit.id)) ids.push(hit.id); }
    else if (!legacy.some(l => sameText(l, name))) legacy.push(name);
  });
  return { ids, legacy };
}

// What the register cell shows: linked project names, then legacy text.
export function projectDisplayList(projectIds, projectsById, legacyText) {
  const names = [];
  (projectIds || []).forEach(id => {
    const p = projectsById.get(Number(id));
    if (p?.name && !names.includes(p.name)) names.push(p.name);
  });
  String(legacyText || '').split(',').map(s => s.trim()).filter(Boolean)
    .forEach(n => { if (!names.some(x => sameText(x, n))) names.push(n); });
  return names.join(', ');
}

export function diffIds(current, next) {
  const cur = new Set((current || []).map(Number));
  const nxt = new Set((next || []).map(Number));
  return {
    add: [...nxt].filter(id => !cur.has(id)),
    remove: [...cur].filter(id => !nxt.has(id)),
  };
}

// ── Comms grouping ───────────────────────────────────────────────────────
// The facts sheet shows collaborators in three places: photographers as plain
// rows under the design team, consultants in one drawer, and contractors,
// vendors and suppliers in another. The press side of Media (magazines, books,
// writers, online, news, events, ...) is credited through publications and is
// left out here, though its register links stay untouched.
export const PHOTO_SPECIALISMS = ['Photographers', 'Video'];
export const CONSULTANT_CATEGORIES = ['Consultants'];
export const TRADE_CATEGORIES = ['Contractor', 'Vendor / Supplier', 'Manufacturer'];

const isPhoto = s => PHOTO_SPECIALISMS.some(p => sameText(p, s));

// Which group a credit belongs to: 'photo', 'consultants', 'trade', or null
// (press, not shown). Decided per link, so a "Specialism not set" firm still
// lands in the drawer of its register category.
export function collaboratorGroup(specialism, vendor, options) {
  if (isPhoto(specialism)) return 'photo';
  const opt = (options || []).find(o => sameText(o.label, specialism));
  const category = opt?.category || vendor?.category || '';
  if (CONSULTANT_CATEGORIES.includes(category)) return 'consultants';
  if (TRADE_CATEGORIES.includes(category)) return 'trade';
  if (category === 'Media') return null;
  return 'trade'; // a firm with no category on the register
}

// Firms offered in a group's pickers: photographers by specialism (whatever
// their category), the two drawers by register category.
export function vendorInGroup(vendor, group) {
  if (group === 'photo') return splitSpecialisms(vendor?.specialism).some(isPhoto);
  if (group === 'consultants') return CONSULTANT_CATEGORIES.includes(vendor?.category);
  if (group === 'trade') return TRADE_CATEGORIES.includes(vendor?.category) || !vendor?.category;
  return false;
}
